import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Gauge,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldHalf,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react'
import DataTable from '../../components/DataTable'
import { useOnChange, useRemembered } from '../../components/listMemory'
import useListParams, { usePageParam } from '../../components/useListParams'
import DateRangeField from '../../components/DateRangeField'
import FilterBar from '../../components/FilterBar'
import PageHeader from '../../components/PageHeader'
import SegmentedControl from '../../components/SegmentedControl'
import StatStrip from '../../components/StatStrip'
import LabelChip from '../../components/LabelChip'
import StatusBadge from '../../components/StatusBadge'
import { useToast } from '../../components/toastContext'
import {
  ANALYSIS_SORTABLE,
  badgeImageUrl,
  getAnalysisPage,
  getAnalysisReport,
  listAnalysis,
  listRiskLevels,
} from './gamificationService'
import { analysisStrings as S } from './strings'
import './gamification.css'
import { serverDetail } from '../../lib/i18n'

/**
 * Risk tone from the levels themselves, not from their names.
 *
 * The previous version keyed `{'خطر منخفض': 'success', …}` on the Arabic labels, so in English
 * every badge fell through to grey — the one column the screen exists for lost its meaning in
 * half the product. Worse, the labels are editable rows in `PointsRiskLevels`: renaming a level
 * broke it in Arabic too.
 *
 * A level carries an id and a `min`, and sorting by `min` IS the severity order — more negative
 * points is more risk. The tone is a position in that order, so it holds for any number of
 * levels, any names, any language.
 *
 * It has to be built from the FULL level list, never from the page's chart: the chart is a
 * `GroupBy` over users, so a band nobody is in is simply absent. Here that leaves medium and
 * high — and scaled against those two alone, medium comes out green.
 */
/* Risk, as a shield: whole, halved, or breached. Keyed on the TONE the ranking
   below already assigned rather than on any wording — the levels are named by
   the client and renaming one must not silently drop its glyph, which is the
   same trap the tone map itself was written to escape. */
const RISK_ICONS = {
  success: ShieldCheck,
  warning: ShieldHalf,
  danger: ShieldAlert,
}

function riskTones(levels) {
  const ordered = [...levels].sort((a, b) => a.min - b.min)
  const tones = new Map()
  ordered.forEach((level, index) => {
    const share = ordered.length < 2 ? 0 : index / (ordered.length - 1)
    tones.set(level.id, share < 0.34 ? 'success' : share < 0.67 ? 'warning' : 'danger')
  })
  return tones
}

/**
 * A points figure in the colour of what it means.
 *
 * The two columns are two totals of opposite sign — points EARNED and points LOST — so the tone
 * belongs to the column, not to the number: both hold positive integers, and `negative: 40`
 * means forty points went away. A row with nothing in a column takes neither tone, because zero
 * is not a finding.
 */
function Points({ value, tone }) {
  return (
    <span className={`gm__points gm__points--${value > 0 ? tone : 'nil'}`} dir="ltr">
      {value.toLocaleString('en-US')}
    </span>
  )
}

/* The list filter binds a NON-nullable bool, so omitting it is not "no filter" — it is
   `false`, an active filter for negative points. Proved against the running API: no value and
   `false` return the same 7 rows where `true` returns 22. There is no "both", so the screen
   asks rather than letting a default decide silently. See API_FINDINGS.md §60. */
const RANKINGS = [
  { id: 'negative', label: S.negative },
  { id: 'positive', label: S.positive },
]

/**
 * What this screen keeps in its own address — the ranking and the period.
 *
 * Neither narrows a list: the ranking swaps which of the two point totals the table is
 * ordered and read by, and the period changes where the summary above it is calculated
 * FROM (see the fetch below). They decide what every figure on screen MEANS, so
 * `?ranking=positive&from=2026-01-01` IS the quarter's earners and can be sent to someone
 * or reloaded onto the same numbers — where as plain state they were lost on every Back,
 * leaving the remembered page pointing into a different dataset. Defaults stay out of the
 * URL. Module-level, as `useListParams` requires.
 */
const LIST_PARAMS = { ranking: 'negative', from: '', to: '' }

/** النتائج — the legacy `PointsAnalysis`. */
export default function AnalysisPage() {
  const toast = useToast()

  const [bands, setBands] = useState([])
  const [levels, setLevels] = useState([])
  const [badgesDirectory, setBadgesDirectory] = useState('BadgesDirectory')
  const [report, setReport] = useState(null)
  /* The whole period's figures and cards, kept to come back to when the dates are cleared. */
  const [period0, setPeriod0] = useState(null)
  const [reporting, setReporting] = useState(true)

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  /* The search is a working position rather than an address — putting it in the URL would
     rewrite history on every keystroke — but it was plain state, so opening a participant
     and coming back returned to a page of a list that was no longer being searched. */
  const [query, setQuery] = useRemembered('gamification-analysis:query', '')
  const [params, setParams] = useListParams(LIST_PARAMS)
  const ranking = params.ranking
  const setRanking = useCallback((next) => setParams({ ranking: next }), [setParams])
  /* Rebuilt from the two strings rather than used as it comes: `useListParams` hands back a
     fresh object whenever ANY parameter changes, the page included, and both the summary
     and the list are fetched off this identity — so paging would refetch the summary. */
  const period = useMemo(() => ({ from: params.from, to: params.to }), [params.from, params.to])
  /* In the address, so Back and a refresh both land on the same page. */
  const [page, setPage] = usePageParam()
  /* How many rows the reader chose to see — not an address, but it was lost on every visit
     to a participant, so a list set to 100 came back at 25. */
  const [pageSize, setPageSize] = useRemembered('gamification-analysis:pageSize', 25)
  /* The order, owned here with the page and sent to the server. Null is the handler's own
     order — the ranking, most points first — which is what a leaderboard opens on. */
  const [sort, setSort] = useRemembered('gamification-analysis:sort', null)

  const hasPeriod = Boolean(period.from || period.to)

  /* The level definitions are read once. The chart says how many people are in a band, the
     level list says which bands exist at all; the chart follows the date range below. */
  useEffect(() => {
    let cancelled = false
    Promise.all([getAnalysisPage(), listRiskLevels({ page: 1, pageSize: 100 })])
      .then(([data, definitions]) => {
        if (cancelled) return
        setBands(data.bands)
        setLevels(definitions.rows)
        setBadgesDirectory(data.badgesDirectory)
        setReport(data.report)
        setPeriod0({ report: data.report, bands: data.bands })
        setReporting(false)
      })
      .catch((error) => {
        if (cancelled) return
        setReporting(false)
        toast.error(serverDetail(error?.message) || S.fail.page)
      })
    return () => {
      cancelled = true
    }
  }, [toast])

  /*
   * A date range narrows the summary AND the cards to the part of the current period between the
   * dates. Clearing the dates goes back to the whole period's figures, read with the page — they
   * used to stay on the last range's.
   */
  useEffect(() => {
    if (!hasPeriod) {
      if (period0) {
        setReport(period0.report)
        setBands(period0.bands)
      }
      return undefined
    }
    let cancelled = false
    setReporting(true)
    getAnalysisReport({ from: period.from, to: period.to })
      .then((data) => {
        if (cancelled) return
        setReport(data.report)
        setBands(data.bands)
        setReporting(false)
      })
      .catch((error) => {
        if (cancelled) return
        setReporting(false)
        toast.error(serverDetail(error?.message) || S.fail.report)
      })
    return () => {
      cancelled = true
    }
  }, [hasPeriod, period.from, period.to, period0, toast])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    listAnalysis({
      page,
      pageSize,
      query,
      from: period.from,
      to: period.to,
      isPositive: ranking === 'positive',
      sort,
    })
      .then((response) => {
        if (cancelled) return
        setRows(response.rows)
        setTotal(response.total)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setTotal(0)
        setLoading(false)
        toast.error(serverDetail(error?.message) || S.fail.list)
      })
    return () => {
      cancelled = true
    }
  }, [page, pageSize, query, period, ranking, sort, toast])

  /* On a CHANGE only: resetting on arrival would discard the remembered page. */
  useOnChange(() => setPage(1), [query, period, ranking])

  const tones = useMemo(() => riskTones(levels), [levels])

  /*
   * A page called النتائج that showed only a table was a list, not a result. These are the
   * ORGANISATION's figures — every participant, not the twenty-five on screen — which is the
   * one thing a client-side sum over the visible page could never be.
   */
  const summary = useMemo(() => {
    if (!report) return []
    return [
      { id: 'people', icon: Users, label: S.summary.people, value: report.users },
      { id: 'positive', icon: TrendingUp, label: S.summary.positive, value: report.positive, tone: 'success' },
      { id: 'negative', icon: TrendingDown, label: S.summary.negative, value: report.negative, tone: 'danger' },
      {
        id: 'average',
        icon: Gauge,
        label: S.summary.average,
        // Already averaged server-side, and it is a decimal — `131.3`, not `131`.
        value: Math.round(report.averageNegative * 10) / 10,
      },
    ]
  }, [report])

  /*
   * The distribution is the actual finding — how the population sits across the bands. It comes
   * from the API over ALL users; the previous version counted the rows it happened to be
   * showing, so paging changed the shape of the organisation.
   *
   * Driven by the level LIST rather than by the chart, so an empty band still shows its zero:
   * "nobody is at high risk" is the best thing this strip can say, and a band that vanishes
   * when it empties says nothing at all. A band the level list has retired is kept only while
   * it still holds people.
   */
  const spread = useMemo(() => {
    const counts = new Map(bands.map((band) => [band.id, band.users]))
    return [...levels]
      .sort((a, b) => a.min - b.min)
      .filter((level) => level.isActive || counts.get(level.id) > 0)
      .map((level) => {
        const tone = tones.get(level.id)
        return {
          id: level.id,
          label: level.name,
          value: counts.get(level.id) ?? 0,
          tone,
          /* The shield says the same thing the colour does, for a reader who
             cannot rely on the colour — and it is what stops four cells reading
             as four anonymous numbers. The levels are named by the client, so
             the glyph is chosen from the TONE the ranking already assigned
             rather than from any particular wording. */
          icon: RISK_ICONS[tone] ?? Shield,
        }
      })
  }, [bands, levels, tones])

  const columns = useMemo(
    () => [
      { key: 'username', header: S.columns.userName, render: (row) => <bdi>{row.username}</bdi> },
      { key: 'name', header: S.columns.name, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'department',
        header: S.columns.department,
        render: (row) => <bdi>{row.department}</bdi>,
      },
      {
        key: 'positive',
        header: S.columns.positive,
        align: 'center',
        render: (row) => <Points value={row.positive} tone="up" />,
      },
      {
        key: 'negative',
        header: S.columns.negative,
        align: 'center',
        render: (row) => <Points value={row.negative} tone="down" />,
      },
      {
        key: 'riskLevel',
        header: S.columns.riskLevel,
        sortable: false,
        render: (row) =>
          row.riskLevel ? (
            <StatusBadge tone={tones.get(row.riskLevelId) ?? 'dark'}>{row.riskLevel}</StatusBadge>
          ) : null,
      },
      {
        /* What the participant has EARNED, beside what they have lost — the row carries them and
           the payload carries the directory to find their pictures in, which is the whole reason
           `BadgesDirectory` is on an otherwise figures-only response. A badge whose image was
           never uploaded shows nothing rather than a broken frame (§58). */
        key: 'badges',
        header: S.columns.badges,
        sortable: false,
        searchable: false,
        /* Every badge by name, its picture beside it when one was uploaded. Only the picture was
           drawn, and hidden when the file was missing — six of ten badges — so a participant
           with "القائد" showed an empty cell, not even the "—" of someone with no badge. */
        render: (row) =>
          row.badges.length ? (
            <span className="gm__badges">
              {row.badges.map((badge) => (
                <span key={badge.id} className="gm__badge-cell">
                  {badge.hasImage && (
                    <img
                      className="gm__badge-img"
                      src={badgeImageUrl(badgesDirectory, badge.id)}
                      alt=""
                      loading="lazy"
                    />
                  )}
                  <LabelChip>{badge.name}</LabelChip>
                </span>
              ))}
            </span>
          ) : (
            <span className="gm__nil">—</span>
          ),
      },
    ],
    [tones, badgesDirectory],
  )
  /* Only what `FilterPointsAnalysisHandler` can order by draws an arrow: the level and the
     badges are worked out after the page arrives. */
  const sortableColumns = useMemo(
    () =>
      columns.map((column) => ({
        ...column,
        sortable: column.sortable !== false && ANALYSIS_SORTABLE.includes(column.key),
      })),
    [columns],
  )

  return (
    <>
      <PageHeader
        title={S.title}
        count={loading ? undefined : total}
        actions={
          report?.riskLevel ? (
            <span className="gm__org-risk">
              <ShieldAlert size={16} strokeWidth={1.75} aria-hidden="true" />
              <span className="gm__org-risk-label">{S.organizationRisk}</span>
              <StatusBadge tone={tones.get(report.riskLevelId) ?? 'dark'}>{report.riskLevel}</StatusBadge>
            </span>
          ) : null
        }
      />

      <FilterBar
        search={{ value: query, onChange: setQuery, placeholder: S.columns.userName }}
        chips={
          hasPeriod
            ? [
                {
                  id: 'period',
                  label: S.period,
                  value: [period.from, period.to].filter(Boolean).join(' — '),
                  onRemove: () => setParams({ from: '', to: '' }),
                },
              ]
            : []
        }
      >
        {/* The legacy `تحديد فترة` opens a daterangepicker behind a button, and its own
            `عرض التقرير` had to be pressed after; the range applies as it is set here.
            `{from, to}` IS the patch `useListParams` takes, so it writes straight to it. */}
        <DateRangeField value={period} onChange={setParams} labels={{ from: S.from, to: S.to }} />
        <SegmentedControl
          items={RANKINGS}
          value={ranking}
          onChange={setRanking}
          ariaLabel={S.pointsType}
        />
      </FilterBar>

      <StatStrip
        items={summary}
        label={hasPeriod ? `${S.summary.caption} — ${S.period}` : `${S.summary.caption} — ${S.allTime}`}
        loading={reporting}
      />

      {spread.length > 0 && <StatStrip items={spread} label={S.spread} variant="band" />}

      <DataTable
        columns={sortableColumns}
        rows={rows}
        loading={loading}
        query={query}
        getRowId={(row) => row.id}
        emptyMessage={S.empty}
        server={{
          page,
          pageSize,
          total,
          sort,
          onChange: (next) => {
            setPage(next.page)
            setPageSize(next.pageSize)
            setSort(next.sort)
          },
        }}
      />
    </>
  )
}
